import Resource from './Resource';
import AppService from 'App/legacy/AppService';
import EventBus from 'App/services/EventBus';
import type { AllIWantData } from 'App/resources/AllIWant';

// AllIWant declares listOnboarding.track as null, but at runtime it is a map of step -> done (see
// AdminDashboard.tsx, which reads it through a non-null assertion). Model the runtime shape here and
// cast at the two boundaries rather than "fixing" the shared declaration — that is a refactor, and
// this is a decouple. The casts go through unknown because the two shapes do not overlap.
type ListOnboarding = AllIWantData['metadata']['listOnboarding'];
type Onboarding = { track?: { [step: string]: boolean }; [key: string]: unknown };

/*
	Standalone Onboarding (WEB-8567). Only update + updateStep are ported — they are the only members
	React reaches, and it reaches them through AccountProfile.save. setListIntro, stepMap and
	stepAction stay on the Angular factory (upsales/common/resources/onboarding.js): setListIntro's
	three callers are all Angular list controllers, stepMap needs $translate plus the AppService.loaded
	hook, and stepAction needs $state, which is still Angular-only.
*/
class OnboardingResource extends Resource {
	constructor() {
		super('onboarding');
	}

	/*
		The Angular action was a bare { method: 'POST' } on URL + API + 'onboarding/' with no
		transformRequest/transformResponse, so _postRequest('') is the faithful equivalent — same URL,
		and with no attributes and no dateFields the base's map/parse hooks are identity. Deliberately
		NOT the inherited save(): that sets opts.isNew, picks PUT/POST off data.id and fires
		_triggerEvent, none of which the Angular action did.
	*/
	update(onboarding: Onboarding) {
		return this._postRequest('', onboarding, { methodName: 'update' }).then(
			(res: { data: { data: Onboarding } }) => {
				const metadata = AppService.getMetadata();
				// Angular read the payload off the $resource envelope as res.data; under axios the
				// envelope itself is res.data, so the payload is res.data.data. Writing res.data here
				// would put the whole { data, error } envelope into metadata.listOnboarding.
				metadata.listOnboarding = res.data.data as unknown as ListOnboarding;

				AppService.setMetadata(metadata);

				// Nothing listens to this channel today. It is behaviour, not decoration — keep it.
				EventBus.broadcast('onboarding.updated', metadata);
			}
		);
	}

	// Returns undefined when the step is already tracked, exactly as Angular did, so callers must not
	// assume a promise.
	updateStep(key: string) {
		const onboarding = AppService.getMetadata()?.listOnboarding as unknown as Onboarding | undefined;
		if (onboarding && onboarding.track && !onboarding.track[key]) {
			onboarding.track[key] = true;

			return this.update(onboarding);
		}
	}
}

export default new OnboardingResource();
